import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  addItemToLocalCart,
  updateLocalCartQuantity,
  removeItemFromLocalCart,
} from "../../redux/locallCartSlice";
import "./index.css";
import { getAvailableDayNames, getCurrentDayName, isItemAvailableToday } from "../../utility/Utility";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

interface Props {
  id: string;
  name: string;
  price: any;
  description: any;
  imageUrl: string;
  portionName?: string;
  additionalCost?: number;
  servingTime?: string;
  availableDays?: number[];
  supportsPreOrdering?: boolean;
  orderClosingTime?: string;
  maxDaysToScheduleAhead?: number;
}

const QuantityChangeButton: React.FC<Props> = ({
  id,
  name,
  price,
  description,
  imageUrl,
  portionName,
  additionalCost,
  servingTime,
  availableDays = [],
  supportsPreOrdering,
  orderClosingTime = "",
  maxDaysToScheduleAhead = 0,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);

  const portionSize = portionName || "SMALL";
  const uniqueId = `${id}-${portionSize}`;
  const finalAdditionalCost = additionalCost ?? 0;
  const finalPrice = price + finalAdditionalCost;
  const cartItem = useSelector((state: RootState) =>
    state.localCartList.localCartList.find((item) => item.id === uniqueId)
  );
  const incremntDisable =
    (description === "CustomMenu" || description === "Custom") &&
    (!cartItem || cartItem.quantity === 0 || cartItem.quantity == null);

  const handleIncrement = () => {
    if (availableDays && !isItemAvailableToday(availableDays)) {
      setShowAvailabilityModal(true);
      return;
    }
    addOrUpdateItem();
  };

  const addOrUpdateItem = () => {
    if (cartItem) {
      dispatch(
        updateLocalCartQuantity({
          id: uniqueId,
          quantity: cartItem.quantity + 1,
        })
      );
    } else {
      dispatch(
        addItemToLocalCart({
          id: uniqueId,
          name,
          price: finalPrice,
          description,
          imageUrl,
          quantity: 1,
          customItems: [],
          portionSize,
          itemType: "",
          extraItems: [],
          servingTime: servingTime,
          supportsPreOrdering: supportsPreOrdering,
          availableDays: availableDays,
          orderClosingTime: orderClosingTime,
          maxDaysToScheduleAhead: maxDaysToScheduleAhead,
        })
      );
    }
  };

  const handleDecrement = () => {
    if (cartItem) {
      if (cartItem.quantity > 1) {
        dispatch(
          updateLocalCartQuantity({
            id: uniqueId,
            quantity: cartItem.quantity - 1,
          })
        );
      } else {
        dispatch(removeItemFromLocalCart({ id: uniqueId }));
      }
    }
  };

  const handleProceed = () => {
    setShowAvailabilityModal(false);
    addOrUpdateItem();
    navigate("/shoppingCart");
  };

  return (
    <>
      <div className="quantity-selector">
        <button
          className="decrement-btn"
          onClick={handleDecrement}
          disabled={!cartItem}
        >
          -
        </button>
        <span className="quantity">{cartItem ? cartItem.quantity : 0}</span>
        <button
          className="increment-btn"
          onClick={handleIncrement}
          disabled={incremntDisable}
        >
          +
        </button>
      </div>

      <Modal
        show={showAvailabilityModal}
        onHide={() => setShowAvailabilityModal(false)}
        centered
        backdrop="static"
        className="availability-modal"
      >
        <Modal.Header closeButton className="availability-modal-header">
          <Modal.Title>Item Not Available Today</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="availability-modal-message">
            {name} is not available on {getCurrentDayName()}.<br />
            Available days: {getAvailableDayNames(availableDays)}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAvailabilityModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="availability-modal-proceed-btn"
            onClick={handleProceed}
          >
            Proceed Anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuantityChangeButton;